import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { TmCart } from '@/tscapi/model/cart';
import { createCartAndAddItem } from '@/tscapi/service/cart.service';
import http from '@/tscapi/service/httpservice';
import { Storage } from '@/tscapi/utils/browser-storage';
import { Cookie } from '@/tscapi/utils/cookie';

import {
    setupAnalyticsData,
    setupAnalyticsRemove,
    setupTruefitUpdateEvent,
    setupAnalyticsUpdateQty,
} from '../utils/analyticsEvents';

type TmCartDerived = {
    isShowBagEmpty: boolean;
};
type TmCartExtended = TmCart & TmCartDerived;
// const cookie: Cookie = new Cookie();
// const headers = {
//     'Content-Type': 'application/json',
//     'cache-control': 'no-cache',
//     currentdatetime: cookie.CurrentDate,
// };

export const useCart = (isAuth: boolean) => {
    const cookie: Cookie = new Cookie();
    return useQuery({
        queryKey: ['bag', cookie.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
        queryFn:
            cookie.UserAccessToken != undefined &&
            cookie.UserAccessToken.Token.length > 0 &&
            cookie.UserAccessToken.Edp > 0
                ? fetchBagAccount
                : fetchBagGuid,
        useErrorBoundary: true,
        retry: 1,
    });

    async function fetchBagAccount(): Promise<TmCart> {
        //signed in
        const base = '/api/v2/';
        const lang = cookie.Language.substring(0, 2) || 'en';
        const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
        if (
            !(
                cookie.UserAccessToken != undefined &&
                cookie.UserAccessToken.Token.length > 0 &&
                cookie.UserAccessToken.Edp > 0
            )
        ) {
            throw new Error('UserToken expired');
        }
        try {
            const response = await http.get(`${base}${lang}/accounts/${userAccessTokenEdp}/cart`);
            //console.log(response.data);
            setupAnalyticsData(response.data);
            return response.data;
        } catch (error) {
            //if this state happens, user will be re-directed to signin page anyway
            console.log('Error getting Shopping Bag for Account');
            // throw new Error('Error getting Shopping Bag for Account');

            return new Promise(function (resolve, reject) {
                resolve({} as TmCart);
            });
        }
    }

    async function fetchBagGuid(): Promise<TmCart> {
        //not signed in
        const base = '/api/v2/';
        const lang = cookie.Language.substring(0, 2) || 'en';
        if (cookie.CartGuid) {
            try {
                const response = await http.get(`${base}${lang}/carts/${cookie.CartGuid}`);
                //console.log(response.data);
                setupAnalyticsData(response.data);
                return response.data;
            } catch (error) {
                console.log(error);
                console.log('GUID failed so clearing bag');
                return new Promise(function (resolve, reject) {
                    resolve({} as TmCart);
                });
            }
        } else {
            cookie.CartProductCount = '0';
            console.log('no GUID exist so clearing bag');
            return new Promise(function (resolve, reject) {
                resolve({} as TmCart);
            });
        }
    }
};

// used to remove donation from bag
// used in order summary
export const useRemoveDonation = (Id, cartGuid) => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const storage = new Storage();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return useMutation({
        mutationFn: () => {
            return http.delete(`${base}${lang}/carts/${cartGuid}/cartlines/${Id}`);
        },
        onSuccess: (response, variables, context) => {
            // update detail view directly
            setupAnalyticsData(response.data);
            cookie.CartGuid = response.data.CartGuid;
            cookie.CartProductCount = response.data.OrderSummary.TotalQuantity.toString();
            storage.deleteSessionLanguageQC();
            if (response?.data?.CartLines && response?.data?.CartLines?.length == 0) {
                //remove redirect if cart is empty
                cookie.deleteTargetUrl();
            }
            queryClient.setQueryData(
                ['bag', response.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                response?.data,
            );
        },
        onError: (error, variables, context) => {
            console.log(`${context} ${error}`);
        },
    });
};

// used to remove items from bag
// used in the Itemcard component
// used in the Notices to remove item if sold out
export const useRemoveItem = (data) => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const storage = new Storage();
    const lang = cookie.Language.substring(0, 2) || 'en';
    return {
        ...useMutation({
            mutationFn: (Id: number) => {
                return http.delete(`${base}${lang}/carts/${data.CartGuid}/cartlines/${Id}`);
            },
            onSuccess: (response, variables, context) => {
                // update detail view directly
                setupAnalyticsRemove(response.data, data, variables);
                setupTruefitUpdateEvent(response.data);
                cookie.CartGuid = response.data.CartGuid;
                cookie.CartProductCount = response.data.OrderSummary.TotalQuantity.toString();
                storage.deleteSessionLanguageQC();

                if (response?.data?.CartLines && response?.data?.CartLines?.length == 0) {
                    //remove redirect if cart is empty
                    cookie.deleteTargetUrl();
                }

                queryClient.setQueryData(
                    ['bag', response.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                    response?.data,
                );
            },
            onError: (error, variables, context) => {
                console.log(`${context} ${error}`);
            },
        }),
    };
};

// used to add donation to bag
// used by donation picker component
// returns bag with donation added
export const useAddItem = (CustomerEdp, Quantity, cartGuid) => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const storage = new Storage();
    const lang = cookie.Language.substring(0, 2) || 'en';

    const createAndAddToCart = (itemEDP) => {
        // not signed in , no cart exists yet
        return createCartAndAddItem({
            Customization: '',
            ItemEDP: itemEDP,
            Quantity: Quantity,
            CustomerEdp: 0,
            //SOPeriod: "D",
            //SOInterval:30,
        });
    };
    const addToExistingCart = (itemEDP) => {
        // signed in
        return http.post(`${base}${lang}/carts/${cartGuid}/cartlines`, {
            CustomerEdp: CustomerEdp,
            Customization: '',
            ItemEDP: itemEDP,
            Quantity: Quantity,
        });
    };
    return useMutation({
        onSuccess: async (response, variables, context) => {
            // update detail view directly
            setupAnalyticsData(response.data);
            cookie.CartGuid = response.data.CartGuid;
            cookie.CartProductCount = response.data.OrderSummary.TotalQuantity.toString();
            storage.deleteSessionLanguageQC();
            //remove other bags that may be cached from previous accounts
            await queryClient.removeQueries({ queryKey: ['bag'] });
            //save new bag
            queryClient.setQueryData(
                ['bag', response.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                response.data as TmCart,
            );
        },
        onError: (error, variables, context) => {
            console.log('error');
        },
        mutationFn: cookie.CartGuid ? addToExistingCart : createAndAddToCart,
    });
};

//
// used to change quantity of item in bag
// used in Itemcard
export const useUpdateCart = (CustomerEdp, cartGuid) => {
    const queryClient = useQueryClient();
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const storage = new Storage();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const updateCart = ({ lineId, edpNo, qty, addRemove }) => {
        return http.put(`${base}${lang}/carts/${cartGuid}/cartlines/${lineId}`, {
            CustomerEdp: CustomerEdp,
            Customization: '',
            ItemEdp: edpNo,
            Quantity: qty,
        });
    };
    return useMutation({
        onMutate: async (data) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['bag'] });

            // Snapshot the previous value
            const previousBag = queryClient.getQueryData<TmCart>([
                'bag',
                cookie.CartGuid,
                cookie.UserAccessToken?.Edp,
                cookie.Language,
            ]);
            const newBag = JSON.parse(JSON.stringify(previousBag));
            newBag.CartLines.find((x) => x.Id == data.lineId).InCart.Quantity = data.qty;

            // Optimistically update to the new value
            if (newBag) {
                queryClient.setQueryData(['bag', cookie.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language], {
                    ...newBag,
                });
            }

            // Return a context object with the snapshotted value
            return { previousBag };
        },
        onSuccess: async (response, variables, context) => {
            setupAnalyticsUpdateQty(response.data, variables, context);
            cookie.CartGuid = response.data.CartGuid;
            cookie.CartProductCount = response.data.OrderSummary.TotalQuantity.toString();
            storage.deleteSessionLanguageQC();
            queryClient.setQueryData(
                ['bag', response.data.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                response.data as TmCart,
            );
        },
        onError: (err, variables, context) => {
            if (context?.previousBag) {
                queryClient.setQueryData<TmCart>(
                    ['bag', cookie.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
                    context.previousBag,
                );
            }
        },
        mutationFn: updateCart,
    });
};

// exprimental - not used
// function to transform incoming new cart to derive new state
// derive if bag should look empty (it excludes donation)
export const transformBagData = (cart: TmCart) => {
    const derivedCart: TmCartExtended = Object.assign({ isShowBagEmpty: true }, cart);
    if (!cart) {
        derivedCart.isShowBagEmpty = true;
    } else {
        if (cart?.CartLines?.length > 0) {
            //cart has products
            if (cart?.CartLines?.length == 1 && cart?.CartLines[0]?.CartLineItem?.IsDonation) {
                //cart one products but only donation
                derivedCart.isShowBagEmpty = true;
            } else {
                derivedCart.isShowBagEmpty = false;
            }
        } else {
            //cart has data but no products
            derivedCart.isShowBagEmpty = true;
        }
    }
    return derivedCart as TmCartExtended;
};
